import { Badge, Button, Card, ConfirmDialog, useToast } from '@citizen-walk/shared-ui';
import { useEffect, useState, type FormEvent } from 'react';
import { useAdminAuth } from '../auth';
import { useDistricts } from '../districts-admin';
import {
  useAdminUsers,
  type AdminUserRow,
  type DisableAdminInput,
  type InviteAdminInput,
  type ResetAdminPasswordInput,
} from './useAdminUsers';
import { useSetRoleClaim, type AssignableRole } from './useSetRoleClaim';

/** 招待 / PW リセットリンクの有効期限プリセット (日)。サーバ側 clamp は 1〜30。 */
const EXPIRY_OPTIONS = [1, 3, 7, 14, 30] as const;
const DEFAULT_EXPIRY_DAYS = 7;

/**
 * 担当地区の割り当て上限に関する平易な注意書き (REQ-4.3.L1 / docs/112 §3-5)。
 *
 * 1 アカウントの担当地区を増やしすぎると、ログイン情報の容量上限により正しく動作しなくなる
 * 既知の制約があるため、UI に常設の注意書きを出す。内部識別子 (クレーム名・バイト数・フィールド名)
 * は出さず、一般の管理者に伝わる表現に翻訳する ([全体ルール: UI 内部 key 非表示])。
 *
 * `count` を渡すと、目安に近づいたときだけ追加で目立つ警告を出す (任意の強調表示、§3-5)。
 */
const DISTRICT_ASSIGN_SOFT_LIMIT = 20;

function DistrictLimitNotice({ count }: { count?: number }) {
  const nearLimit = typeof count === 'number' && count >= DISTRICT_ASSIGN_SOFT_LIMIT - 3;
  return (
    <div className="mb-2 grid grid-cols-1 gap-1">
      <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded p-2 leading-relaxed">
        💡
        1人の管理者に担当地区を多く割り当てすぎると、システム上の制限により正しく動作しなくなることがあります。担当地区はできるだけ少なく（目安：20地区以内）に保ち、多くの地区を扱う場合は管理者を分けてください。
      </p>
      {nearLimit && (
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 leading-relaxed">
          ⚠️ 現在 {count}{' '}
          地区を選択中です。目安（20地区）に近づいています。これ以上増やす場合は、別の管理者に分けることを検討してください。
        </p>
      )}
    </div>
  );
}

/** ISO8601 → 日本語の日時表記 (「○○まで有効」表示用)。 */
function formatExpiry(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * ユーザー管理画面 (Admin Wave AW3、docs/108 Critical #5,#6,#7 + High)。
 *
 * old2 apps/admin/src/pages/AdminUsersPage.tsx を v2 に移植。master が管理者を
 * 一覧 / 招待 / 担当地区インライン編集 / 無効化・有効化 / PW リセットする。
 *
 * old2 からの差分:
 *   - 一覧の真実源を Firestore `adminUsers` collection → Auth Custom Claims (listAdmins) に変更
 *     (v2 に adminUsers collection が無いため)。
 *   - 「招待」フォーム + role 付与は v2 既存の good-diff (confirm / toast / loading / error 状態) を継承。
 *   - 担当地区編集 / role 変更は v2 既存 `setRoleClaim` を再利用 (old2 `setAdminRole` 相当)。
 *   - native confirm() → 無効化/有効化のみ shared-ui ConfirmDialog に格上げ。
 *
 * 認可: 全 callable が server 側 requireMaster。UI でも master 以外は操作 disable。
 */
export function UsersAdminPage() {
  const { state: authState } = useAdminAuth();
  const { toast } = useToast();
  const districts = useDistricts();
  const isMaster = authState.role === 'master';

  const {
    state: listState,
    refresh,
    disableAdmin,
    deleteAdmin,
    resetAdminPassword,
    inviteAdmin,
  } = useAdminUsers();

  // setRoleClaim を 1 つ用意してロジックを共有 (担当地区編集 / role 変更で再利用、old2 setAdminRole 相当)。
  const { call: changeRoleCall } = useSetRoleClaim();

  // 招待フォーム
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'master' | 'district'>('district');
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>([]);
  const [inviteExpiryDays, setInviteExpiryDays] = useState<number>(DEFAULT_EXPIRY_DAYS);
  const [inviting, setInviting] = useState(false);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [inviteExpiresAt, setInviteExpiresAt] = useState<string | null>(null);

  // 無効化/有効化の確認ダイアログ
  const [pendingDisable, setPendingDisable] = useState<AdminUserRow | null>(null);
  // 削除の確認ダイアログ (完全削除 = 復元不可)
  const [pendingDelete, setPendingDelete] = useState<AdminUserRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (isMaster) void refresh();
  }, [isMaster, refresh]);

  function toggleDistrict(id: string) {
    setSelectedDistricts((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id],
    );
  }

  function districtLabel(id: string): string {
    if (districts.status !== 'ready') return id;
    return districts.items.find((d) => d.id === id)?.name ?? id;
  }

  async function copyLink(link: string) {
    try {
      await navigator.clipboard.writeText(link);
      toast('success', 'リンクをコピーしました');
    } catch {
      toast('error', 'コピーに失敗しました (手動で選択してください)');
    }
  }

  // ─────────────────────────────────────────── 招待 (inviteAdmin)
  async function handleInvite(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    if (!email.trim()) {
      toast('error', 'email を入力してください');
      return;
    }
    if (role === 'district' && selectedDistricts.length === 0) {
      toast('error', '担当地区を 1 つ以上選択してください');
      return;
    }
    const confirmMsg = `${email} を role=${role}${
      role === 'district' ? ` (担当地区: ${selectedDistricts.map(districtLabel).join(', ')})` : ''
    } で招待します。よろしいですか？`;
    if (!confirm(confirmMsg)) return;

    setInviting(true);
    setInviteLink(null);
    setInviteExpiresAt(null);
    try {
      const input: InviteAdminInput = {
        email: email.trim(),
        role,
        districts: role === 'district' ? selectedDistricts : [],
        expiresInDays: inviteExpiryDays,
      };
      const res = await inviteAdmin(input);
      setInviteLink(res.link);
      setInviteExpiresAt(res.expiresAt);
      toast(
        'success',
        res.created ? `${res.email} を新規作成し招待しました` : `${res.email} を招待しました`,
      );
      setEmail('');
      setSelectedDistricts([]);
      await refresh();
    } catch (e) {
      toast('error', `招待に失敗しました: ${(e as Error).message}`);
    } finally {
      setInviting(false);
    }
  }

  // ─────────────────────────────────────────── 担当地区 / role 変更 (setRoleClaim 再利用)
  async function handleChangeRole(target: AdminUserRow, newRole: AssignableRole, dists: string[]) {
    try {
      await changeRoleCall({ email: target.email, role: newRole, districts: dists });
      toast('success', `${target.email} を更新しました`);
      await refresh();
      return true;
    } catch (e) {
      const raw = (e as Error).message ?? '';
      // setRoleClaim の self-downgrade ガード (failed-precondition) を日本語化。
      const message = raw.includes('Self-downgrade')
        ? '自分自身のロールは降格できません (マスター管理者の自己ロック防止)。'
        : `更新に失敗しました: ${raw}`;
      toast('error', message);
      return false;
    }
  }

  // ─────────────────────────────────────────── 無効化/有効化 (disableAdmin)
  function requestToggleDisabled(target: AdminUserRow) {
    setPendingDisable(target);
  }
  async function confirmToggleDisabled() {
    const target = pendingDisable;
    setPendingDisable(null);
    if (!target) return;
    const nextDisabled = !target.disabled;
    try {
      const input: DisableAdminInput = { uid: target.uid, disabled: nextDisabled };
      await disableAdmin(input);
      toast('success', `${target.email} を${nextDisabled ? '無効化' : '有効化'}しました`);
      await refresh();
    } catch (e) {
      toast('error', `操作に失敗しました: ${(e as Error).message}`);
    }
  }

  // ─────────────────────────────────────────── 削除 (deleteAdmin、完全削除)
  function requestDelete(target: AdminUserRow) {
    setPendingDelete(target);
  }
  async function confirmDelete() {
    const target = pendingDelete;
    if (!target || deleting) return; // 二重クリック再入ガード (ConfirmDialog に busy prop が無いため)
    setDeleting(true);
    try {
      await deleteAdmin({ uid: target.uid, email: target.email });
      toast('success', `${target.email || target.uid} を削除しました`);
      setPendingDelete(null);
      await refresh();
    } catch (e) {
      const raw = (e as Error).message ?? '';
      // サーバ側ガード (failed-precondition) を日本語化。
      let message = `削除に失敗しました: ${raw}`;
      if (raw.includes('protected')) {
        message = 'この管理者は保護対象 (初代マスター) のため削除できません。';
      } else if (raw.includes('yourself')) {
        message = '自分自身は削除できません (自己ロック防止)。';
      } else if (raw.includes('not an admin')) {
        message = '対象は管理者ではないため、この操作では削除できません。';
      }
      toast('error', message);
    } finally {
      setDeleting(false);
    }
  }

  // ─────────────────────────────────────────── PW リセット (resetAdminPassword)
  async function handleResetPassword(
    target: AdminUserRow,
    expiresInDays: number,
  ): Promise<{ link: string; expiresAt: string } | null> {
    try {
      const input: ResetAdminPasswordInput = {
        email: target.email,
        uid: target.uid,
        expiresInDays,
      };
      const res = await resetAdminPassword(input);
      return { link: res.link, expiresAt: res.expiresAt };
    } catch (e) {
      toast('error', `リセットリンクの生成に失敗しました: ${(e as Error).message}`);
      return null;
    }
  }

  return (
    <>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">管理者ユーザー</h1>
        <p className="text-sm text-slate-500 mt-1">
          REQ-4.3.1-2 — master / district admin の一覧・招待・担当地区編集・無効化・PW
          リセット。Firebase Auth Custom Claims が真実源です。
        </p>
        {isMaster && (
          <p className="text-xs text-slate-600 mt-2 bg-slate-50 border border-slate-200 rounded p-2">
            💡 管理者がパスワードを忘れた場合は、対象の行の「PW
            リセット」から再発行リンクを発行し、本人にメール等で送ってください。本人がリンクを開くと、その場でパスワードを再設定してサインインできます。
          </p>
        )}
      </header>

      {!isMaster && (
        <Card padding="md" className="border-danger-600 mb-4">
          <p className="text-sm text-danger-700">
            マスター管理者のみアクセスできます (現在: role={authState.role ?? 'none'})。
          </p>
        </Card>
      )}

      {/* ─────────────── 新規招待 ─────────────── */}
      <Card padding="lg" className="mb-6">
        <h2 className="text-base font-bold text-slate-900 mb-3">新規招待</h2>
        <form onSubmit={handleInvite} className="grid grid-cols-1 gap-3">
          <label className="grid grid-cols-1 gap-1">
            <span className="text-xs text-slate-500">メールアドレス *</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={!isMaster || inviting}
              placeholder="user@example.com"
              className="min-h-touch px-3 border border-slate-300 rounded text-sm"
            />
            <span className="text-[11px] text-slate-400">
              Auth に未登録の email は仮パスワードで新規作成し、パスワード設定リンクを発行します。
            </span>
          </label>

          <label className="grid grid-cols-1 gap-1">
            <span className="text-xs text-slate-500">ロール *</span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as 'master' | 'district')}
              disabled={!isMaster || inviting}
              className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
            >
              <option value="district">地区管理者 (district)</option>
              <option value="master">マスター管理者 (master)</option>
            </select>
          </label>

          {role === 'district' && (
            <fieldset className="grid grid-cols-1 gap-1 border border-slate-200 rounded p-3">
              <legend className="text-xs text-slate-500 px-1">担当地区 (1 つ以上選択) *</legend>
              <DistrictLimitNotice count={selectedDistricts.length} />
              {districts.status === 'loading' && (
                <p className="text-xs text-slate-500">地区一覧読込中…</p>
              )}
              {districts.status === 'error' && (
                <p className="text-xs text-danger-700">地区一覧読込エラー</p>
              )}
              {districts.status === 'ready' && districts.items.length === 0 && (
                <p className="text-xs text-slate-500">地区が登録されていません</p>
              )}
              {districts.status === 'ready' &&
                districts.items.map((d) => (
                  <label key={d.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedDistricts.includes(d.id)}
                      onChange={() => toggleDistrict(d.id)}
                      disabled={!isMaster || inviting}
                    />
                    <span className="truncate">
                      {d.name} <code className="text-[10px] text-slate-400">{d.id}</code>
                    </span>
                  </label>
                ))}
            </fieldset>
          )}

          <label className="grid grid-cols-1 gap-1">
            <span className="text-xs text-slate-500">招待リンクの有効期限</span>
            <select
              value={inviteExpiryDays}
              onChange={(e) => setInviteExpiryDays(Number(e.target.value))}
              disabled={!isMaster || inviting}
              className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white w-40"
            >
              {EXPIRY_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  {d} 日間{d === DEFAULT_EXPIRY_DAYS ? '（既定）' : ''}
                </option>
              ))}
            </select>
            <span className="text-[11px] text-slate-400">
              本人がリンクを開いてパスワードを設定できる期間です（1〜30 日）。
            </span>
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="submit" disabled={!isMaster || inviting}>
              {inviting ? '送信中…' : '招待する'}
            </Button>
          </div>
        </form>

        {inviteLink && (
          <div className="mt-3 bg-primary-50 border border-primary-200 rounded p-3 text-sm">
            <p className="mb-1 font-medium text-xs text-slate-700">招待リンク (パスワード設定):</p>
            <code className="block break-all text-xs text-slate-800">{inviteLink}</code>
            <Button
              type="button"
              size="sm"
              className="mt-2"
              onClick={() => void copyLink(inviteLink)}
            >
              コピー
            </Button>
            <div className="mt-2 text-[11px] text-slate-600 space-y-0.5">
              <p>📨 このリンクをコピーして、招待先の本人にメール等で送ってください。</p>
              <p>
                本人がリンクを開いてパスワードを設定すると、そのまま管理画面にサインインできます。
              </p>
              {inviteExpiresAt && (
                <p className="text-amber-700">
                  ⏳ 有効期限: <strong>{formatExpiry(inviteExpiresAt)}</strong>{' '}
                  まで。期限切れの場合は再度「招待する」で発行し直してください。
                </p>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* ─────────────── 登録済み管理者 一覧 ─────────────── */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold text-slate-900">登録済み管理者</h2>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => void refresh()}
          disabled={!isMaster || listState.status === 'loading'}
        >
          {listState.status === 'loading' ? '更新中…' : '再読込'}
        </Button>
      </div>

      {listState.status === 'error' && (
        <Card padding="md" className="border-danger-600 mb-4">
          <p className="text-sm text-danger-700">
            一覧取得に失敗しました: {listState.error.message}
          </p>
        </Card>
      )}

      {(listState.status === 'loading' || listState.status === 'idle') &&
        listState.admins.length === 0 && <p className="text-sm text-slate-500">読み込み中…</p>}

      {listState.status === 'ready' && listState.admins.length === 0 && (
        <p className="text-sm text-slate-500">管理者はまだいません。</p>
      )}

      {listState.admins.length > 0 && (
        <ul className="grid grid-cols-1 gap-2">
          {listState.admins.map((a) => (
            <AdminRowItem
              key={a.uid}
              admin={a}
              districts={districts.status === 'ready' ? districts.items : []}
              isMaster={isMaster}
              isSelf={a.uid === authState.user?.uid}
              districtLabel={districtLabel}
              onChangeRole={handleChangeRole}
              onToggleDisabled={requestToggleDisabled}
              onDelete={requestDelete}
              onResetPassword={handleResetPassword}
            />
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={pendingDisable !== null}
        intent={pendingDisable?.disabled ? 'warning' : 'danger'}
        title={pendingDisable?.disabled ? '管理者を有効化' : '管理者を無効化'}
        message={
          pendingDisable
            ? `${pendingDisable.email} を${pendingDisable.disabled ? '有効化' : '無効化'}します。よろしいですか？`
            : ''
        }
        confirmLabel={pendingDisable?.disabled ? '有効化' : '無効化'}
        onConfirm={() => void confirmToggleDisabled()}
        onCancel={() => setPendingDisable(null)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        intent="danger"
        title="管理者を削除"
        message={
          pendingDelete
            ? `${pendingDelete.email || pendingDelete.uid} を完全に削除します。この操作は取り消せません（アカウント自体が削除されます）。よろしいですか？`
            : ''
        }
        confirmLabel={deleting ? '削除中…' : '削除する'}
        onConfirm={() => void confirmDelete()}
        onCancel={() => {
          if (!deleting) setPendingDelete(null);
        }}
      />
    </>
  );
}

/**
 * 管理者 1 人分の行 (old2 AdminRowItem 踏襲)。master は inline でロール (地区管理者⇄マスター管理者)
 * と担当地区を編集可能。自分自身 (isSelf) は降格できない (サーバ側 self-downgrade ガードと一致)。
 */
function AdminRowItem({
  admin: a,
  districts,
  isMaster,
  isSelf,
  districtLabel,
  onChangeRole,
  onToggleDisabled,
  onDelete,
  onResetPassword,
}: {
  admin: AdminUserRow;
  districts: { id: string; name: string }[];
  isMaster: boolean;
  isSelf: boolean;
  districtLabel: (id: string) => string;
  onChangeRole: (
    target: AdminUserRow,
    role: AssignableRole,
    districts: string[],
  ) => Promise<boolean>;
  onToggleDisabled: (target: AdminUserRow) => void;
  onDelete: (target: AdminUserRow) => void;
  onResetPassword: (
    target: AdminUserRow,
    expiresInDays: number,
  ) => Promise<{ link: string; expiresAt: string } | null>;
}) {
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [draftRole, setDraftRole] = useState<'master' | 'district'>(a.role);
  const [checkedIds, setCheckedIds] = useState<string[]>(a.districts);
  const [saving, setSaving] = useState(false);
  const [resetExpiryDays, setResetExpiryDays] = useState<number>(DEFAULT_EXPIRY_DAYS);
  const [resetResult, setResetResult] = useState<{ link: string; expiresAt: string } | null>(null);
  const [resetting, setResetting] = useState(false);

  function toggleDistrict(id: string, checked: boolean) {
    setCheckedIds((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  }

  function startEditing() {
    setDraftRole(a.role);
    setCheckedIds(a.districts);
    setEditing(true);
  }

  async function saveRole() {
    if (draftRole === 'district' && checkedIds.length === 0) {
      // 0 件保存ガード (old2 踏襲)。master では districts 不要。
      alert('担当地区を 1 つ以上選択してください。');
      return;
    }
    setSaving(true);
    try {
      const ok = await onChangeRole(a, draftRole, draftRole === 'district' ? checkedIds : []);
      if (ok) setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    setResetting(true);
    setResetResult(null);
    try {
      const res = await onResetPassword(a, resetExpiryDays);
      if (res) setResetResult(res);
    } finally {
      setResetting(false);
    }
  }

  async function copyLink(link: string) {
    try {
      await navigator.clipboard.writeText(link);
      toast('success', 'リンクをコピーしました');
    } catch {
      toast('error', 'コピーに失敗しました (リンクを手動で選択してコピーしてください)');
    }
  }

  return (
    <li
      className={`bg-white border rounded p-4 border-slate-200 ${a.disabled ? 'opacity-60' : ''}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-medium text-slate-900 truncate">{a.email || '(email なし)'}</p>
          <p className="text-xs text-slate-500 flex items-center gap-1 flex-wrap mt-0.5">
            <span>{a.role === 'master' ? 'マスター' : '地区管理者'}</span>
            {a.disabled && (
              <Badge variant="danger" size="sm">
                無効
              </Badge>
            )}
          </p>

          {!editing && (
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {a.role === 'district' && (
                <p className="text-xs text-slate-500">
                  担当: {a.districts.map((id) => districtLabel(id)).join(', ') || '—'}
                </p>
              )}
              {isMaster && (
                <button
                  type="button"
                  onClick={startEditing}
                  className="text-xs text-primary-600 hover:underline"
                >
                  ロール / 担当地区を編集
                </button>
              )}
            </div>
          )}

          {editing && (
            <div className="mt-2 border border-slate-200 rounded p-3">
              <label className="grid grid-cols-1 gap-1 mb-3">
                <span className="text-xs font-medium text-slate-600">ロール</span>
                <select
                  value={draftRole}
                  onChange={(e) => setDraftRole(e.target.value as 'master' | 'district')}
                  disabled={saving}
                  className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
                >
                  {/* 自分自身は地区管理者へ降格できない (サーバ側 self-downgrade ガードと一致)。 */}
                  <option value="district" disabled={isSelf}>
                    地区管理者 (district)
                  </option>
                  <option value="master">マスター管理者 (master)</option>
                </select>
                {isSelf && (
                  <span className="text-[11px] text-amber-600">
                    自分自身はマスター管理者から降格できません (自己ロック防止)。
                  </span>
                )}
              </label>

              {draftRole === 'district' && (
                <>
                  <p className="text-xs font-medium text-slate-600 mb-2">担当地区を選択:</p>
                  <DistrictLimitNotice count={checkedIds.length} />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 mb-3">
                    {districts.length === 0 && (
                      <p className="text-xs text-slate-500">地区が登録されていません</p>
                    )}
                    {districts.map((d) => (
                      <label key={d.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checkedIds.includes(d.id)}
                          onChange={(e) => toggleDistrict(d.id, e.target.checked)}
                          className="w-4 h-4"
                        />
                        <span className="truncate">{d.name}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}

              {draftRole === 'master' && (
                <p className="text-xs text-slate-500 mb-3">
                  マスター管理者は全地区にアクセスできます (担当地区の指定は不要です)。
                </p>
              )}

              <div className="flex gap-2">
                <Button type="button" size="sm" onClick={() => void saveRole()} disabled={saving}>
                  {saving ? '保存中…' : '保存'}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => setEditing(false)}
                  disabled={saving}
                >
                  キャンセル
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1 shrink-0">
          <Button
            type="button"
            size="sm"
            variant={a.disabled ? 'primary' : 'danger'}
            onClick={() => onToggleDisabled(a)}
            disabled={!isMaster}
          >
            {a.disabled ? '有効化' : '無効化'}
          </Button>
          <div className="flex items-center gap-1">
            <select
              value={resetExpiryDays}
              onChange={(e) => setResetExpiryDays(Number(e.target.value))}
              disabled={!isMaster || resetting || !a.email}
              aria-label="リセットリンクの有効期限"
              title="リセットリンクの有効期限"
              className="text-xs px-1 py-1 border border-slate-300 rounded bg-white disabled:opacity-50"
            >
              {EXPIRY_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  {d}日
                </option>
              ))}
            </select>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => void handleReset()}
              disabled={!isMaster || resetting || !a.email}
            >
              {resetting ? '生成中…' : 'PW リセット'}
            </Button>
          </div>
          {/* 完全削除 (deleteAdmin)。保護対象 (初代マスター) と自分自身は不可。
              サーバ側でも isProtectedAdminEmail / 自己削除ガードで二重に防止。 */}
          <Button
            type="button"
            size="sm"
            variant="danger"
            onClick={() => onDelete(a)}
            disabled={!isMaster || a.protected === true || isSelf}
            title={
              a.protected === true
                ? '初代マスター管理者は削除できません'
                : isSelf
                  ? '自分自身は削除できません'
                  : undefined
            }
          >
            削除
          </Button>
        </div>
      </div>

      {(a.protected === true || isSelf) && isMaster && (
        <p className="mt-2 text-[11px] text-slate-400">
          {a.protected === true
            ? '※ 初代マスター管理者のため削除できません。'
            : '※ 自分自身は削除できません (自己ロック防止)。'}
        </p>
      )}

      {resetResult && (
        <div className="mt-3 bg-primary-50 border border-primary-200 rounded p-3 text-sm">
          <p className="mb-1 font-medium text-xs text-slate-700">パスワードリセットリンク:</p>
          <code className="block break-all text-xs text-slate-800">{resetResult.link}</code>
          <Button
            type="button"
            size="sm"
            className="mt-2"
            onClick={() => void copyLink(resetResult.link)}
          >
            コピー
          </Button>
          <div className="mt-2 text-[11px] text-slate-600 space-y-0.5">
            <p>📨 このリンクをコピーして、本人にメール等で送ってください。</p>
            <p className="text-amber-700">
              ⏳ 有効期限: <strong>{formatExpiry(resetResult.expiresAt)}</strong>{' '}
              まで。期限切れの場合は再度「PW リセット」で発行し直してください。
            </p>
          </div>
        </div>
      )}
    </li>
  );
}
